/* ======================================================================
   WSPÓLNE
   ====================================================================== */
"use strict";

/* .pad na .catalog-filter */
function updateCatalogFilterPadding() {
    const bar = document.querySelector(".catalog__toolbar .catalog-filter");
    if (!bar) return;

    const hasChips = bar.querySelector(".chip");
    bar.classList.toggle("pad", !!hasChips);
}

/* Utilities */
const $  = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
const on = (el, ev, fn, opts) => el?.addEventListener(ev, fn, opts);
const off = (el, ev, fn, opts) => el?.removeEventListener(ev, fn, opts);
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const prefersReduced = () =>
    matchMedia("(prefers-reduced-motion: reduce)").matches;

const toTokens = (val) =>
    String(val ?? "")
        .toLowerCase()
        .split(/[,\s|;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

const setMH = (el, val) =>
    el.style.setProperty("max-height", val, "important");

const measure = (el) => {
    const prevVal = el.style.getPropertyValue("max-height");
    const prevPri = el.style.getPropertyPriority("max-height");
    const wasHidden = el.hidden;

    if (wasHidden) el.hidden = false;
    setMH(el, "none");

    const h = el.scrollHeight;

    if (prevVal) el.style.setProperty("max-height", prevVal, prevPri || "");
    else el.style.removeProperty("max-height");

    if (wasHidden) el.hidden = true;
    return h;
};

const animateOpen = (el) => {
    if (prefersReduced()) {
        el.hidden = false;
        return;
    }
    el.hidden = false;
    el.classList.add("is-animating");

    const h = measure(el);

    setMH(el, "0px");
    el.style.paddingBlock = "0";

    requestAnimationFrame(() => {
        setMH(el, h + "px");
        el.style.paddingBlock = "";
    });

    const tidy = (e) => {
        if (e.propertyName !== "max-height") return;
        el.style.removeProperty("max-height");
        el.classList.remove("is-animating");
        el.style.paddingBlock = "";
        off(el, "transitionend", tidy);
    };
    on(el, "transitionend", tidy);
};

const animateClose = (el) => {
    if (prefersReduced()) {
        el.hidden = true;
        return;
    }
    el.classList.add("is-animating");

    const h = measure(el);
    setMH(el, h + "px");

    requestAnimationFrame(() => {
        setMH(el, "0px");
        el.style.paddingBlock = "0";
    });

    const tidy = (e) => {
        if (e.propertyName !== "max-height") return;
        el.hidden = true;
        el.style.removeProperty("max-height");
        el.classList.remove("is-animating");
        el.style.paddingBlock = "";
        off(el, "transitionend", tidy);
    };
    on(el, "transitionend", tidy);
};

/* ======================================================================
   TREEVIEW (.category)
   ====================================================================== */
function initTreeViews() {
    const STORAGE_NS = "category_tree_state_v2";
    const FOCUSABLE = ".category__link, .category__toggle";
    const trees = $$(".category[role='tree']");
    if (!trees.length) return;

    trees.forEach((tree, idx) => initTree(tree, buildKey(tree, idx)));

    function buildKey(tree, index) {
        const name =
            tree.getAttribute("data-tree-key") ||
            tree.id ||
            `tree-${index}`;
        return `${STORAGE_NS}:${location.pathname}:${name}`;
    }

    function initTree(TREE, KEY) {
        const pairs = () =>
            $$(".category__toggle[aria-controls]", TREE)
                .map((btn) => {
                    const id = btn.getAttribute("aria-controls");
                    const panel = id && $("#" + CSS.escape(id), TREE);
                    return panel ? { btn, panel, id } : null;
                })
                .filter(Boolean);

        const focusables = () =>
            $$(FOCUSABLE, TREE).filter((n) => n.offsetParent !== null);
        const isOpenBtn = (btn) =>
            btn.getAttribute("aria-expanded") === "true";
        const setExpanded = (li, v) =>
            li.setAttribute("aria-expanded", String(v));

        const save = () => {
            const expanded = $$(".category__item[aria-expanded='true']", TREE)
                .map((li) =>
                    $(".category__toggle", li)?.getAttribute(
                        "aria-controls"
                    )
                )
                .filter(Boolean);
            localStorage.setItem(KEY, JSON.stringify({ expanded }));
        };
        const read = () => {
            try {
                const { expanded = [] } = JSON.parse(
                    localStorage.getItem(KEY) || "{}"
                );
                return Array.isArray(expanded) ? expanded : [];
            } catch {
                return [];
            }
        };

        const expand = (btn, panel, { anim = true, persist = true } = {}) => {
            const li = btn.closest(".category__item");
            if (!li || !panel) return;
            btn.setAttribute("aria-expanded", "true");
            setExpanded(li, true);
            panel.setAttribute("aria-hidden", "true");
            if (anim) animateOpen(panel);
            else panel.hidden = false;
            panel.removeAttribute("aria-hidden");
            if (persist) save();
        };

        const collapse = (
            btn,
            panel,
            { anim = true, persist = true } = {}
        ) => {
            const li = btn.closest(".category__item");
            if (!li || !panel) return;
            btn.setAttribute("aria-expanded", "false");
            setExpanded(li, false);
            panel.setAttribute("aria-hidden", "true");
            if (anim) animateClose(panel);
            else panel.hidden = true;
            if (persist) save();
        };

        const toggle = (btn, panel, opts) =>
            isOpenBtn(btn)
                ? collapse(btn, panel, opts)
                : expand(btn, panel, opts);

        const collapseAll = () => {
            pairs().forEach(({ btn, panel }) => {
                btn.setAttribute("aria-expanded", "false");
                setExpanded(btn.closest(".category__item"), false);
                panel.hidden = true;
                panel.style.maxHeight = "";
                panel.style.paddingBlock = "";
            });
        };

        const markChainChecked = (start) => {
            let n = start;
            while (n && n.classList?.contains("category__item")) {
                n.classList.add("checked");
                n = n.parentElement?.closest(".category__item");
            }
        };

        const openPathTo = (el) => {
            if (!el) return;
            let p = el.closest(".category__item");
            while (p) {
                const b = $(".category__toggle", p);
                if (b) {
                    const id = b.getAttribute("aria-controls");
                    const pnl = id && $("#" + CSS.escape(id), TREE);
                    if (pnl && pnl.hidden)
                        expand(b, pnl, { anim: false, persist: false });
                }
                p = p.parentElement?.closest(".category__item");
            }
        };

        const markFromHash = () => {
            const id = decodeURIComponent(location.hash.slice(1));
            if (!id) return false;
            const t =
                document.getElementById(id) ||
                $(
                    `.category__toggle[aria-controls="${CSS.escape(
                        id
                    )}"]`,
                    TREE
                ) ||
                $(`#${CSS.escape(id)} .category__link`, TREE);
            if (!t) return false;
            openPathTo(t);
            const it = t.closest(".category__item");
            if (it) markChainChecked(it);
            t.scrollIntoView({ block: "nearest" });
            t.focus({ preventScroll: true });
            return true;
        };

        on(TREE, "click", (e) => {
            const btn = e.target.closest(".category__toggle");
            if (btn && TREE.contains(btn)) {
                const id = btn.getAttribute("aria-controls");
                const pnl = id && $("#" + CSS.escape(id), TREE);
                if (pnl) toggle(btn, pnl, { anim: true, persist: true });
                return;
            }
            const link = e.target.closest(".category__link");
            if (link && TREE.contains(link)) {
                const href = link.getAttribute("href");
                if (!href || href === "#") e.preventDefault();
            }
        });

        let typeBuf = "",
            typeTimer = 0;
        const resetType = () => {
            typeBuf = "";
            clearTimeout(typeTimer);
            typeTimer = 0;
        };

        on(TREE, "keydown", (e) => {
            const t = e.target;
            if (!t.closest(".category")) return;

            const all = focusables();
            const idx = all.indexOf(t);
            const prev = all[clamp(idx - 1, 0, all.length - 1)];
            const next = all[clamp(idx + 1, 0, all.length - 1)];

            const item = t.closest(".category__item");
            const btn = $(".category__toggle", item);
            const id = btn && btn.getAttribute("aria-controls");
            const pnl = id && $("#" + CSS.escape(id), TREE);

            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    (next || all.at(-1))?.focus();
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    (prev || all[0])?.focus();
                    break;
                case "ArrowRight":
                    e.preventDefault();
                    if (btn && pnl && btn.getAttribute("aria-expanded") !== "true")
                        expand(btn, pnl, { anim: true, persist: true });
                    else if (pnl && !pnl.hidden) $(FOCUSABLE, pnl)?.focus();
                    break;
                case "ArrowLeft":
                    e.preventDefault();
                    if (btn && pnl && btn.getAttribute("aria-expanded") === "true")
                        collapse(btn, pnl, { anim: true, persist: true });
                    else {
                        const parentItem =
                            item.parentElement?.closest(".category__item");
                        const parentFocus =
                            parentItem &&
                            ($(".category__link", parentItem) ||
                                $(".category__toggle", parentItem));
                        parentFocus?.focus();
                    }
                    break;
                case "Enter":
                case " ":
                    if (btn && t === btn) {
                        e.preventDefault();
                        toggle(btn, pnl, { anim: true, persist: true });
                    } else if (t.matches(".category__link")) {
                        const href = t.getAttribute("href");
                        if (!href || href === "#") e.preventDefault();
                    }
                    break;
                default: {
                    if (
                        e.key.length === 1 &&
                        !e.ctrlKey &&
                        !e.metaKey &&
                        !e.altKey
                    ) {
                        typeBuf += e.key.toLowerCase();
                        clearTimeout(typeTimer);
                        typeTimer = setTimeout(resetType, 600);
                        const container = item.parentElement;
                        const siblings = $$(
                            ":scope > .category__item",
                            container
                        );
                        const candidates = siblings
                            .map(
                                (li) =>
                                    $(".category__link", li) ||
                                    $(".category__toggle", li)
                            )
                            .filter(Boolean);
                        const found = candidates.find((n) =>
                            (n.textContent || "")
                                .trim()
                                .toLowerCase()
                                .startsWith(typeBuf)
                        );
                        if (found) {
                            e.preventDefault();
                            found.focus();
                        }
                    }
                }
            }
        });

        collapseAll();
        const saved = new Set(read());
        pairs().forEach(({ btn, panel, id }) => {
            if (saved.has(id))
                expand(btn, panel, { anim: false, persist: false });
        });

        const hashDone = markFromHash();
        if (!hashDone) {
            const current = $("[aria-current='page']", TREE);
            if (current) {
                openPathTo(current);
                const it = current.closest(".category__item");
                if (it) markChainChecked(it);
                current.scrollIntoView({
                    block: "nearest",
                    inline: "nearest",
                });
            } else {
                const preset = $(
                    ".category__item--active .category__link",
                    TREE
                );
                const it = preset?.closest(".category__item");
                if (it) markChainChecked(it);
            }
        }

        on(window, "beforeunload", save);
    }
}

/* ======================================================================
   FILTERS UI (accordion + URL preset)
   ====================================================================== */
function initFiltersAccordion() {
    const root = $(".filters");
    if (!root) return;

    on(root, "click", (e) => {
        const btn = e.target.closest(".filters__toggle");
        if (!btn) return;
        const id = btn.getAttribute("aria-controls");
        const panel = id && $("#" + CSS.escape(id), root);
        if (!panel) return;

        const expanded = btn.getAttribute("aria-expanded") === "true";
        btn.setAttribute("aria-expanded", String(!expanded));
        expanded ? animateClose(panel) : animateOpen(panel);
    });

    (function presetFromURL() {
        const params = new URLSearchParams(window.location.search);
        if (![...params.keys()].length) return;

        const unique = Array.from(new Set([...params.keys()]));
        unique.forEach((name) => {
            const values = params
                .getAll(name)
                .flatMap((v) => String(v).split(/[,\s]+/))
                .map((v) => v.trim().toLowerCase())
                .filter(Boolean);

            if (!values.length) return;

            const inputs = root.querySelectorAll(
                `.f-check__input[name="${CSS.escape(name)}"]`
            );
            inputs.forEach(
                (inp) =>
                    (inp.checked = values.includes(
                        String(inp.value).toLowerCase()
                    ))
            );
        });

        root.dispatchEvent(
            new Event("filters:preset", { bubbles: true })
        );
        const any = $(".f-check__input", root);
        if (any) any.dispatchEvent(new Event("change", { bubbles: true }));
    })();
}

/* ======================================================================
   PRODUCTS (checkbox filters + chips + empty state) – moduł .filters
   ====================================================================== */
function initCheckboxProductsModule() {
    const filtersRoot = $('.filters[data-filters-key="products"]');
    const chipsBar = $(".catalog__toolbar .catalog-filter");
    const grid = $(".catalog__grid");
    const products = Array.from(document.querySelectorAll("[data-product]"));

    if (!filtersRoot || !chipsBar || !grid || products.length === 0) return;

    const ensureId = (inp, ix) => {
        if (inp.id) return inp.id;
        const newId = `${inp.name}-${String(inp.value).replace(
            /\W+/g,
            "_"
        )}-${ix}`;
        inp.id = newId;
        return newId;
    };
    $$(".f-check__input", filtersRoot).forEach(ensureId);

    const CLOSE_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
  <path d="M5.7 5.7L8.3 8.3M8.3 5.7L5.7 8.3M7 1a6 6 0 1 0 0 12A6 6 0 0 0 7 1Z"
        stroke="#B9B9B9" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
</svg>`.trim();

    const getActiveFilters = () => {
        const map = new Map();
        filtersRoot
            .querySelectorAll(".f-check__input:checked")
            .forEach((inp) => {
                const key =
                    inp.closest(".filters__group")?.dataset.group ||
                    inp.name;
                if (!map.has(key)) map.set(key, new Set());
                map.get(key).add(String(inp.value));
            });
        return map;
    };

    const productMatches = (el, criteria) => {
        for (const [key, set] of criteria.entries()) {
            if (!set || set.size === 0) continue;
            const pvals = toTokens(el.dataset[key]);
            if (!pvals.length) return false;
            const ok = pvals.some((v) => set.has(v));
            if (!ok) return false;
        }
        return true;
    };

    const emptyState = (() => {
        let el = $(".catalog__empty", grid.parentElement);
        if (!el) {
            el = document.createElement("div");
            el.className = "catalog__empty";
            el.style.display = "none";
            el.textContent =
                "Brak produktów spełniających wybrane filtry.";
            grid.after(el);
        }
        return el;
    })();

    const applyFilters = () => {
        const active = getActiveFilters();
        let visible = 0;

        products.forEach((p) => {
            const ok = productMatches(p, active);
            p.classList.toggle("is-hidden", !ok);
            p.toggleAttribute("hidden", !ok);
            p.setAttribute("aria-hidden", String(!ok));
            if (ok) visible++;
        });

        emptyState.style.display = visible ? "none" : "";
        return visible;
    };

    const updateFacetCounts = () => {
        const active = getActiveFilters();

        filtersRoot
            .querySelectorAll(".filters__group")
            .forEach((groupEl) => {
                const groupKey = groupEl.dataset.group;
                const inputs =
                    groupEl.querySelectorAll(".f-check__input");

                const other = new Map(
                    Array.from(active.entries()).filter(
                        ([k]) => k !== groupKey
                    )
                );

                inputs.forEach((inp) => {
                    const test = new Map(other);
                    test.set(groupKey, new Set([String(inp.value)]));

                    const count = products.reduce(
                        (acc, el) =>
                            acc +
                            (productMatches(el, test) ? 1 : 0),
                        0
                    );

                    const countEl = inp
                        .closest(".f-check")
                        ?.querySelector(".f-check__count");
                    if (countEl) countEl.textContent = `(${count})`;

                    inp.disabled = !inp.checked && count === 0;
                });
            });
    };

    const getGroupTitle = (groupEl) =>
        groupEl.querySelector(".filters__title")?.textContent.trim() ||
        groupEl.dataset.group ||
        "";

    const ensureChipGroup = (key, title) => {
        let group = chipsBar.querySelector(
            `[data-chip-group="${key}"]`
        );
        if (group) return group;
        group = document.createElement("div");
        group.className = "catalog-filter__group";
        group.dataset.chipGroup = key;
        group.innerHTML = `
        <span class="catalog-filter__label">${title}:</span>
        <div class="catalog-filter__chips" role="list"></div>`;
        chipsBar.appendChild(group);
        return group;
    };

    const addChipForInput = (input) => {
        const groupEl = input.closest(".filters__group");
        const key = groupEl?.dataset.group || input.name;
        const title = getGroupTitle(groupEl);
        const label =
            input
                .closest(".f-check")
                ?.querySelector(".f-check__text")
                ?.textContent.trim() || input.value;
        const chipKey = `${input.name}=${input.value}`;

        const box = ensureChipGroup(key, title).querySelector(
            ".catalog-filter__chips"
        );
        if (box.querySelector(`[data-chip="${chipKey}"]`)) return;

        const chip = document.createElement("span");
        chip.className = "chip chip--removable";
        chip.dataset.chip = chipKey;
        chip.dataset.inputId = input.id;
        chip.innerHTML = `
        <span class="chip__text">${label}</span>
        <button type="button" class="chip__remove" aria-label="Usuń filtr ${label}">${CLOSE_SVG}</button>`;
        box.appendChild(chip);
    };

    const removeChipForInput = (input) => {
        const chipKey = `${input.name}=${input.value}`;
        const chip = chipsBar.querySelector(
            `[data-chip="${chipKey}"]`
        );
        if (!chip) return;
        const group = chip.closest("[data-chip-group]");
        chip.remove();
        if (group && !group.querySelector(".chip")) group.remove();
    };

    const syncChipsFromState = () => {
        chipsBar
            .querySelectorAll("[data-chip-group]")
            .forEach((n) => n.remove());
        filtersRoot
            .querySelectorAll(".f-check__input:checked")
            .forEach(addChipForInput);
        updateCatalogFilterPadding();
    };

    filtersRoot.addEventListener("change", (e) => {
        const input = e.target.closest(".f-check__input");
        if (!input) return;

        input.checked
            ? addChipForInput(input)
            : removeChipForInput(input);

        applyFilters();
        updateFacetCounts();
        updateCatalogFilterPadding();
    });

    chipsBar.addEventListener("click", (e) => {
        const btn = e.target.closest(".chip__remove");
        if (!btn) return;
        const chip = btn.closest(".chip");
        const input = document.getElementById(chip.dataset.inputId);
        if (input) {
            input.checked = false;
            input.dispatchEvent(
                new Event("change", { bubbles: true })
            );
        } else {
            chip.remove();
            updateCatalogFilterPadding();
        }
    });

    const applyBtn = document.querySelector(
        ".catalog-menu__item-footer .btn"
    );
    on(applyBtn, "click", () => {
        applyFilters();
        updateFacetCounts();
        updateCatalogFilterPadding();
    });

    syncChipsFromState();
    applyFilters();
    updateFacetCounts();
}


/* ======================================================================
   MULTISELECT Z SELECTÓW + CHIPSY + FILTROWANIE PRODUKTÓW (karty)
   ====================================================================== */
function initCardMultiselectFilters() {
    const FILTER_SELECT_SELECTOR = "select.js-nice";
    const SORT_SELECT_ID = "sort";
    const CLEAR_BUTTON_ID = "clear-filters";
    const PRODUCT_SELECTOR = "[data-product]";
    const CHOSED_FORM_ID = "choosed-filters";

    const FILTER_MAP = {
        color: "color",
        mount: "mount",
        schedule: "schedule",
        category: "type",
        size: "size",
        power: "power",
        available: "available",
    };

    const productsContainer = document.querySelector(".catalog__grid");
    const products = Array.from(
        document.querySelectorAll(PRODUCT_SELECTOR)
    );
    const clearBtn = document.getElementById(CLEAR_BUTTON_ID);
    const chipsRoot = document.getElementById(CHOSED_FORM_ID);
    const sortSelect = document.getElementById(SORT_SELECT_ID);

    if (!productsContainer || !products.length || !chipsRoot) return;

    products.forEach((card, index) => {
        card.dataset.index = index;
    });

    const activeFilters = new Map();

    const hasAnyFilters = () => {
        for (const set of activeFilters.values()) {
            if (set && set.size) return true;
        }
        return false;
    };

    const getFilterSet = (key) => {
        if (!activeFilters.has(key)) {
            activeFilters.set(key, new Set());
        }
        return activeFilters.get(key);
    };

    const removeFilterValue = (key, value) => {
        const set = activeFilters.get(key);
        if (!set) return;
        set.delete(value);
        if (!set.size) {
            activeFilters.delete(key);
        }
    };

    /* Chips UI */
    const clearChipsUI = () => {
        chipsRoot.innerHTML = "";
        chipsRoot.style.display = "none";
        updateCatalogFilterPadding();
    };

    const ensureGroup = (key, labelText) => {
        let group = chipsRoot.querySelector(
            `[data-filter-group="${key}"]`
        );
        if (group) return group;

        group = document.createElement("div");
        group.className = "choosed-filters__group";
        group.dataset.filterGroup = key;
        group.innerHTML = `
            <span class="choosed-filters__label">${labelText}:</span>
            <div class="choosed-filters__chips" role="list"></div>
        `;
        chipsRoot.appendChild(group);
        return group;
    };

    const addChip = (key, value, select) => {
        const label =
            select.dataset.placeholder ||
            select.dataset.label ||
            select.getAttribute("aria-label") ||
            select.name ||
            key;

        const option =
            select.querySelector(
                `option[value="${CSS.escape(value)}"]`
            ) || select.options[select.selectedIndex];

        const valueLabel = option
            ? option.textContent.trim()
            : String(value).trim();

        const group = ensureGroup(key, label);
        const box = group.querySelector(".choosed-filters__chips");

        if (
            box.querySelector(
                `.chip[data-filter-key="${key}"][data-filter-value="${CSS.escape(
                    value
                )}"]`
            )
        ) {
            return;
        }

        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "chip chip--filter";
        chip.setAttribute("role", "listitem");
        chip.dataset.filterKey = key;
        chip.dataset.filterValue = value;

        chip.innerHTML = `
            <span class="chip__text">${valueLabel}</span>
            <span class="chip__remove" aria-hidden="true">×</span>
            <span class="visually-hidden">Usuń filtr ${valueLabel}</span>
        `;

        box.appendChild(chip);
        chipsRoot.style.display = "";
        updateCatalogFilterPadding();
    };

    const removeChipFromUI = (key, value) => {
        const group = chipsRoot.querySelector(
            `[data-filter-group="${key}"]`
        );
        if (!group) return;

        const chip = group.querySelector(
            `.chip[data-filter-key="${key}"][data-filter-value="${CSS.escape(
                value
            )}"]`
        );
        if (chip) chip.remove();

        if (!group.querySelector(".chip")) {
            group.remove();
        }

        if (!chipsRoot.querySelector(".chip")) {
            chipsRoot.style.display = "none";
        }

        updateCatalogFilterPadding();
    };

    const rebuildChipsFromState = () => {
        clearChipsUI();
        for (const [key, set] of activeFilters.entries()) {
            const select = document.getElementById(key);
            if (!select) continue;
            for (const value of set.values()) {
                addChip(key, value, select);
            }
        }
        if (hasAnyFilters()) {
            chipsRoot.style.display = "";
        }
        updateCatalogFilterPadding();
    };

    chipsRoot.addEventListener("click", (e) => {
        const chip = e.target.closest(".chip");
        if (!chip || !chipsRoot.contains(chip)) return;

        const key = chip.dataset.filterKey;
        const value = chip.dataset.filterValue;

        removeFilterValue(key, value);
        chip.remove();

        const group = chipsRoot.querySelector(
            `[data-filter-group="${key}"]`
        );
        if (group && !group.querySelector(".chip")) {
            group.remove();
        }
        if (!chipsRoot.querySelector(".chip")) {
            chipsRoot.style.display = "none";
        }

        const select = document.getElementById(key);
        if (select) {
            const wrapper = select.closest(".c-select");
            if (wrapper) {
                const optBtn = wrapper.querySelector(
                    `.c-select__option[data-value="${CSS.escape(
                        value
                    )}"]`
                );
                if (optBtn) {
                    optBtn.classList.remove("is-active");
                    optBtn.setAttribute("aria-selected", "false");
                }
            }
        }

        applyFilters();
        updateCatalogFilterPadding();
    });

    /* Filtracja kart */
    const emptyState = (() => {
        let el = document.querySelector(".catalog__empty");
        if (!el) {
            el = document.createElement("div");
            el.className = "catalog__empty";
            el.style.display = "none";
            el.textContent =
                "Brak produktów spełniających wybrane filtry.";
            productsContainer.after(el);
        }
        return el;
    })();

    const productMatchesFilters = (card) => {
        if (!activeFilters.size) return true;

        for (const [key, set] of activeFilters.entries()) {
            if (!set || !set.size) continue;

            const attrName = FILTER_MAP[key] || key;
            const productVal = card.dataset[attrName];
            const tokens = toTokens(productVal);

            if (!tokens.length) return false;

            const ok = tokens.some((token) => set.has(token));
            if (!ok) return false;
        }

        return true;
    };

    const applyFilters = () => {
        let visible = 0;

        products.forEach((card) => {
            const ok = productMatchesFilters(card);
            card.classList.toggle("is-hidden", !ok);
            card.toggleAttribute("hidden", !ok);
            card.setAttribute("aria-hidden", String(!ok));
            if (ok) visible++;
        });

        emptyState.style.display = visible ? "none" : "";
        return visible;
    };

    /* Sortowanie */
    const compareBy = (a, b, key, asc = true) => {
        const va = a.dataset[key];
        const vb = b.dataset[key];

        if (va == null || vb == null) return 0;

        if (key === "price") {
            const na = parseFloat(String(va).replace(",", "."));
            const nb = parseFloat(String(vb).replace(",", "."));
            if (isNaN(na) || isNaN(nb)) return 0;
            return asc ? na - nb : nb - na;
        } else {
            return asc
                ? String(va).localeCompare(String(vb), "pl", {
                    numeric: true,
                })
                : String(vb).localeCompare(String(va), "pl", {
                    numeric: true,
                });
        }
    };

    const applySorting = (sortValue) => {
        if (!sortValue || !productsContainer) return;

        const sorted = products.slice();

        switch (sortValue) {
            case "default":
                sorted.sort(
                    (a, b) =>
                        (parseInt(a.dataset.index, 10) || 0) -
                        (parseInt(b.dataset.index, 10) || 0)
                );
                break;
            case "price-asc":
                sorted.sort((a, b) =>
                    compareBy(a, b, "price", true)
                );
                break;
            case "price-desc":
                sorted.sort((a, b) =>
                    compareBy(a, b, "price", false)
                );
                break;
            case "name-asc":
                sorted.sort((a, b) =>
                    compareBy(a, b, "name", true)
                );
                break;
            case "name-desc":
                sorted.sort((a, b) =>
                    compareBy(a, b, "name", false)
                );
                break;
            default:
                break;
        }

        sorted.forEach((card) => {
            productsContainer.appendChild(card);
        });
    };

    if (sortSelect) {
        sortSelect.addEventListener("change", () => {
            applySorting(sortSelect.value || "default");
        });
    }

    /* Obsługa selectów (multiselect) */
    const allSelects = Array.from(
        document.querySelectorAll(FILTER_SELECT_SELECTOR)
    );

    allSelects.forEach((select) => {
        const id = select.id;

        if (id === SORT_SELECT_ID) return;

        select.addEventListener("change", () => {
            const value = select.value;
            if (!value) return;

            const key = id || select.name;
            if (!key) return;

            const set = getFilterSet(key);

            if (set.has(value)) {
                // OFF
                removeFilterValue(key, value);
                removeChipFromUI(key, value);

                const wrapper = select.closest(".c-select");
                if (wrapper) {
                    const optBtn = wrapper.querySelector(
                        `.c-select__option[data-value="${CSS.escape(
                            value
                        )}"]`
                    );
                    if (optBtn) {
                        optBtn.classList.remove("is-active");
                        optBtn.setAttribute(
                            "aria-selected",
                            "false"
                        );
                    }
                }
            } else {
                // ON
                set.add(value);
                addChip(key, value, select);

                const wrapper = select.closest(".c-select");
                if (wrapper) {
                    const optBtn = wrapper.querySelector(
                        `.c-select__option[data-value="${CSS.escape(
                            value
                        )}"]`
                    );
                    if (optBtn) {
                        optBtn.classList.add("is-active");
                        optBtn.setAttribute("aria-selected", "true");
                    }
                }
            }

            applyFilters();
            select.value = ""; // wróć do placeholdera
        });
    });

    /* Wyczyść wszystkie */
    if (clearBtn) {
        clearBtn.addEventListener("click", (e) => {
            e.preventDefault();

            activeFilters.clear();

            document
                .querySelectorAll(".c-select__option.is-active")
                .forEach((btn) => {
                    btn.classList.remove("is-active");
                    btn.setAttribute("aria-selected", "false");
                });

            allSelects.forEach((select) => {
                if (select.id === SORT_SELECT_ID) {
                    if (
                        select.querySelector(
                            'option[value="default"]'
                        )
                    ) {
                        select.value = "default";
                    } else {
                        select.selectedIndex = 0;
                    }
                } else {
                    select.value = "";
                }
            });

            clearChipsUI();
            applyFilters();

            if (sortSelect) {
                sortSelect.dispatchEvent(
                    new Event("change", { bubbles: true })
                );
            }

            updateCatalogFilterPadding();
        });
    }

    // stan początkowy
    applyFilters();
    if (sortSelect) {
        applySorting(sortSelect.value || "default");
    }
    rebuildChipsFromState();
}

/* ======================================================================
   CUSTOM SELECT UI – sort = single, reszta = multi (toggle)
   ====================================================================== */
function initCustomSelects() {
    const SORT_SELECT_ID = "sort";

    const toLabel = (select) =>
        select.dataset.placeholder ||
        select.getAttribute("aria-label") ||
        (select.options[0]?.textContent.trim()) ||
        "";

    const initCustomSelect = (select) => {
        if (select.closest(".c-select")) return;

        const isSort = select.id === SORT_SELECT_ID;
        const placeholder = toLabel(select);

        const wrapper = document.createElement("div");
        wrapper.className = "c-select";

        select.parentNode.insertBefore(wrapper, select);
        wrapper.appendChild(select);

        select.classList.add("c-select__native");

        const trigger = document.createElement("button");
        trigger.type = "button";
        trigger.className = "c-select__trigger";
        trigger.setAttribute("aria-haspopup", "listbox");
        trigger.setAttribute("aria-expanded", "false");

        if (isSort && select.value) {
            const currentOpt = select.options[select.selectedIndex];
            trigger.textContent = currentOpt
                ? currentOpt.textContent.trim()
                : placeholder;
        } else {
            trigger.textContent = placeholder;
        }

        wrapper.insertBefore(trigger, select);

        const menu = document.createElement("div");
        menu.className = "c-select__menu";
        menu.setAttribute("role", "listbox");

        Array.from(select.options).forEach((opt) => {
            const value = opt.value;
            const text = opt.textContent.trim();

            if (!value && !isSort) return;

            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = "c-select__option";
            btn.setAttribute("role", "option");
            btn.dataset.value = value;
            btn.textContent = text;

            if (isSort && value === select.value) {
                btn.classList.add("is-active");
                btn.setAttribute("aria-selected", "true");
            }

            menu.appendChild(btn);
        });

        wrapper.insertBefore(menu, select);

        const markSelectedSingle = (value) => {
            menu.querySelectorAll(".c-select__option").forEach((btn) => {
                const isActive = btn.dataset.value === value;
                btn.classList.toggle("is-active", isActive);
                btn.setAttribute(
                    "aria-selected",
                    isActive ? "true" : "false"
                );
                if (isActive) {
                    trigger.textContent = btn.textContent.trim();
                }
            });
        };

        const openMenu = () => {
            wrapper.classList.add("is-open");
            trigger.setAttribute("aria-expanded", "true");
        };
        const closeMenu = () => {
            wrapper.classList.remove("is-open");
            trigger.setAttribute("aria-expanded", "false");
        };

        trigger.addEventListener("click", () => {
            wrapper.classList.contains("is-open")
                ? closeMenu()
                : openMenu();
        });

        menu.addEventListener("click", (e) => {
            const optionBtn = e.target.closest(".c-select__option");
            if (!optionBtn) return;

            const value = optionBtn.dataset.value;

            if (isSort) {
                select.value = value;
                markSelectedSingle(value);
                select.dispatchEvent(
                    new Event("change", { bubbles: true })
                );
                closeMenu();
            } else {
                const isActive =
                    optionBtn.classList.contains("is-active");

                if (isActive) {
                    optionBtn.classList.remove("is-active");
                    optionBtn.setAttribute(
                        "aria-selected",
                        "false"
                    );
                } else {
                    optionBtn.classList.add("is-active");
                    optionBtn.setAttribute("aria-selected", "true");
                }

                select.value = value;
                select.dispatchEvent(
                    new Event("change", { bubbles: true })
                );

                closeMenu();
            }
        });

        document.addEventListener("click", (e) => {
            if (!wrapper.contains(e.target)) {
                closeMenu();
            }
        });

        if (isSort) {
            select.addEventListener("change", () => {
                markSelectedSingle(select.value);
            });
        }

        if (select.value) {
            if (isSort) {
                markSelectedSingle(select.value);
            } else {
                const btn = menu.querySelector(
                    `.c-select__option[data-value="${CSS.escape(
                        select.value
                    )}"]`
                );
                if (btn) {
                    btn.classList.add("is-active");
                    btn.setAttribute("aria-selected", "true");
                }
            }
        }
    };

    document
        .querySelectorAll("select.js-nice")
        .forEach(initCustomSelect);
}

/* ======================================================================
   CATEGORY MENU – akordeon z toggle wewnątrz <a>
   ====================================================================== */
function initCategoryMenuAccordion() {
    const root = document.querySelector('.category-menu');
    if (!root) return;

    const parentItems = root.querySelectorAll('.category-menu__item--parent');

    const getSubmenu = (item) =>
        item.querySelector(':scope > .category-menu__list');

    const getToggle = (item) =>
        item.querySelector(':scope > .category-menu__link .category-menu__toggle');

    const setItemState = (item, open) => {
        const submenu = getSubmenu(item);
        const toggle  = getToggle(item);
        if (!submenu || !toggle) return;

        if (open) {
            item.classList.add('is-open');
            submenu.classList.remove('hidden');
            submenu.classList.add('visible');
            toggle.textContent = '−';
            toggle.setAttribute('aria-expanded', 'true');
            toggle.setAttribute('aria-label', 'Zwiń kategorię');
        } else {
            item.classList.remove('is-open');
            submenu.classList.add('hidden');
            submenu.classList.remove('visible');
            toggle.textContent = '+';
            toggle.setAttribute('aria-expanded', 'false');
            toggle.setAttribute('aria-label', 'Rozwiń kategorię');
        }
    };

    const closeSiblings = (item) => {
        const parentList = item.parentElement;
        if (!parentList) return;

        parentList
            .querySelectorAll(':scope > .category-menu__item--parent')
            .forEach((sibling) => {
                if (sibling !== item) {
                    setItemState(sibling, false);
                }
            });
    };

    parentItems.forEach((item) => {
        const link    = item.querySelector(':scope > .category-menu__link');
        const submenu = getSubmenu(item);
        if (!submenu || !link) return;

        // utwórz toggle wewnątrz <a>, jeśli go nie ma
        let toggle = link.querySelector('.category-menu__toggle');
        if (!toggle) {
            toggle = document.createElement('span');
            toggle.className = 'category-menu__toggle';
            toggle.setAttribute('role', 'button');
            toggle.setAttribute('aria-haspopup', 'true');
            toggle.setAttribute('tabindex', '0');
            link.appendChild(toggle);
        }

        // stan startowy – na podstawie klas w HTML
        const initiallyOpen =
            submenu.classList.contains('visible') &&
            !submenu.classList.contains('hidden');
        setItemState(item, initiallyOpen);

        // klik w toggle = tylko rozwijanie/zamykanie
        on(toggle, 'click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const isOpen = item.classList.contains('is-open');
            if (!isOpen) {
                closeSiblings(item);
                setItemState(item, true);
            } else {
                setItemState(item, false);
            }
        });

        // wsparcie klawiatury na toggle (Enter / Spacja)
        on(toggle, 'keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle.click();
            }
        });

        // klik w tekst linka – normalne przejście do kategorii (nic nie robimy)
    });
}

/* ======================================================================
   START
   ====================================================================== */
document.addEventListener("DOMContentLoaded", () => {
    initTreeViews();
    initFiltersAccordion();
    initCheckboxProductsModule();
    initCardMultiselectFilters();
    initCustomSelects();
    initCategoryMenuAccordion();
});
